import React, { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { format, parseISO } from "date-fns";

import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Chip,
  Grid,
  useTheme,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  PersonOutline as PersonOutlineIcon,
  WorkOutline as WorkOutlineIcon,
} from "@mui/icons-material";

import { RootState } from "../../../store/Index";
import { tokens } from "../../../theme";
import { PilotProps } from "../types/view-pilot";
import {
  CREW_DOCUMENT_CATEGORIES,
  CrewDocumentRecord,
  getDocumentExtension,
  getDocumentName,
} from "../types/crew-documents";
import { WorkExperiences } from "../types/view-pilot";
import { downloadDocument } from "../utils/crewDocuments";
import { DocumentViewerDialog } from "../components/DocumentViewerDialog";

import {
  CheckIcon,
  DocumentIcon,
  DownloadIcon,
  EnvelopeIcon,
  ExportIcon,
  LocationIcon,
  PhoneIcon,
  RatingIcon,
  SkillsIcon,
  WarningIcon,
} from "../../../components/icons";

export interface UserDetails {
  id: number;
  user_infor: {
    browser?: string | null;
    operating_system?: string | null;
    device_type?: string | null;
    has_pixel_pilot_device?: boolean | null;
    device_information?: {
      platform?: string | null;
      manufacturer?: string | null;
      device_model?: string | null;
      os_version?: string | null;
      app_version?: string | null;
    } | null;
    location: string | null;
  };
}

/** Dates arrive as ISO strings; anything unparseable falls back to the raw value. */
const formatMonth = (value: string | null, fallback: string): string => {
  if (!value) return fallback;
  try {
    return format(parseISO(value), "MMM yyyy");
  } catch {
    return value;
  }
};

/** Most recent role first; an ongoing role (no end date) outranks a finished one. */
const byMostRecent = (a: WorkExperiences, b: WorkExperiences) => {
  if (!a.end_date !== !b.end_date) return a.end_date ? 1 : -1;
  return (b.start_date ?? "").localeCompare(a.start_date ?? "");
};

interface PilotOverviewProps {
  pilot: PilotProps;
  userDetails: UserDetails | null;
  documents?: CrewDocumentRecord[];
  /** Jumps to the Documents tab, where documents can be managed. */
  onManageDocuments?: () => void;
}

export const PilotOverview: React.FC<PilotOverviewProps> = ({
  pilot,
  userDetails,
  documents = [],
  onManageDocuments,
}) => {
  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const backgroundColor = colors.primary[800] || "#A9B2C3";

  const [viewerIndex, setViewerIndex] = useState<number | null>(null);

  // Embedded in the crew member response — `sort` mutates, so copy first.
  const workExperience = useMemo(
    () => [...(pilot?.workExperiences ?? [])].sort(byMostRecent),
    [pilot?.workExperiences],
  );

  if (!pilot) return null;

  const experienceYears = pilot.experience_years ?? 0;

  return (
    <>
      <DocumentViewerDialog
        open={viewerIndex !== null}
        onClose={() => setViewerIndex(null)}
        documents={documents}
        activeIndex={viewerIndex ?? 0}
        onNavigate={setViewerIndex}
      />

      <Grid container spacing={2}>
      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0 }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                <PersonOutlineIcon color="disabled" sx={{ fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  About
                </Typography>
              </Box>
              <Chip
                label={
                  experienceYears > 0
                    ? `${experienceYears} ${experienceYears === 1 ? "year" : "years"} of experience`
                    : "Experience not specified"
                }
                size="small"
                color={experienceYears > 0 ? "info" : "default"}
                variant={experienceYears > 0 ? "filled" : "outlined"}
                sx={{ borderRadius: 1, flexShrink: 0 }}
              />
            </Box>

            {pilot.bio?.trim() ? (
              <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
                {pilot.bio}
              </Typography>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No bio provided yet.
              </Typography>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <SkillsIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Contact Information
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <EnvelopeIcon color="disabled" sx={{ fontSize: 18 }} />
              <Typography variant="body2">{pilot?.email}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={1}>
              <PhoneIcon color="disabled" sx={{ fontSize: 18 }} />
              <Typography variant="body2">{pilot?.phone}</Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
            <LocationIcon color="disabled" sx={{ fontSize: 18 }} />

            {userDetails?.user_infor?.location ? (
              <Typography variant="body2">
                {userDetails.user_infor.location}
              </Typography>
            ) : (
              <Chip
                label={<i>Location Not Provided</i>}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  borderColor: colors.primary[800],
                }}
              />
            )}
          </Box>

            <Box sx={{ padding: 1, borderRadius: 1, bgcolor: backgroundColor }}>
              <Box
                display={"flex"}
                alignItems={"center"}
                justifyContent={"space-between"}
                mb={1}
              >
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  Emergency Contact
                </Typography>
              </Box>
              <Typography variant="subtitle2">
                {pilot.emergencyContactName || "Unnamed Emergency Contact"}
              </Typography>

              <Box display="flex" alignItems="center" gap={1}>
                <PhoneIcon color="disabled" sx={{ fontSize: 18 }} />
                <Typography variant="body2">
                  {pilot.emergencyContact}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              justifyContent={"space-between"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <Box display={"flex"} alignItems={"center"} gap={"4px"}>
                <DocumentIcon color="disabled" sx={{ fontSize: 20 }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Documents
                </Typography>
              </Box>
              {onManageDocuments && (
                <Button
                  size="small"
                  onClick={onManageDocuments}
                  sx={{ textTransform: "none" }}
                >
                  Manage
                </Button>
              )}
            </Box>

            {documents.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={1}>
                No documents uploaded yet.
              </Typography>
            ) : (
              documents.map((record, index) => (
                <Box
                  display={"flex"}
                  key={record.id}
                  alignItems={"center"}
                  justifyContent={"space-between"}
                  gap={1}
                  sx={{
                    padding: 1,
                    mb: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: backgroundColor,
                  }}
                >
                  <Box
                    display={"flex"}
                    alignItems={"center"}
                    gap={1}
                    sx={{ minWidth: 0 }}
                  >
                    <Box
                      display={"flex"}
                      alignItems={"center"}
                      justifyContent={"center"}
                      sx={{
                        width: 36,
                        height: 36,
                        borderRadius: "50%",
                        padding: 1,
                        bgcolor: backgroundColor,
                        flexShrink: 0,
                      }}
                    >
                      <DocumentIcon color="disabled" sx={{ fontSize: 20 }} />
                    </Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant={"body2"} fontWeight={600}>
                        {record.type_name}
                      </Typography>
                      <Typography
                        variant={"caption"}
                        color="text.secondary"
                        noWrap
                        display="block"
                        title={getDocumentName(record.document)}
                      >
                        {getDocumentName(record.document)} ·{" "}
                        {getDocumentExtension(record.document)}
                      </Typography>
                    </Box>
                  </Box>
                  <Box display={"flex"} alignItems={"center"} flexShrink={0}>
                    <Tooltip title="View">
                      <IconButton onClick={() => setViewerIndex(index)}>
                        <ExportIcon color="disabled" sx={{ fontSize: 18 }} />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Download">
                      <IconButton
                        onClick={() => downloadDocument(record.document)}
                      >
                        <DownloadIcon color="info" sx={{ fontSize: 20 }} />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <SkillsIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Skills & Equipment
              </Typography>
            </Box>

            <Typography
              variant="subtitle2"
              mb={1}
              sx={{ color: colors.primary[100] }}
            >
              Skills
            </Typography>
            {pilot.skills?.length === 0 ? (
              <Chip
                label={<i>No Skills Specified</i>}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  mb: 2,
                  borderColor: colors.primary[800],
                }}
              />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {pilot.skillsDetail.map((skill) => (
                  <Chip
                    key={skill.id}
                    label={skill.name}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      borderColor: colors.primary[800],
                    }}
                  />
                ))}
              </Box>
            )}

            <Typography
              variant="subtitle2"
              mb={1}
              sx={{ color: colors.primary[100] }}
            >
              Equipment
            </Typography>
            {pilot.tools?.length === 0 ? (
              <Chip
                label={<i>No Tools Specified</i>}
                size="small"
                variant="outlined"
                sx={{
                  borderRadius: 1,
                  borderColor: colors.primary[800],
                }}
              />
            ) : (
              <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
                {pilot.toolsDetail.map((tool) => (
                  <Chip
                    key={tool.id}
                    label={tool.name}
                    size="small"
                    variant="outlined"
                    sx={{
                      borderRadius: 1,
                      borderColor: colors.primary[800],
                    }}
                  />
                ))}
              </Box>
            )}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0, height: "100%" }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <RatingIcon color="disabled" sx={{ fontSize: 22 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Certifications Checklist
              </Typography>
            </Box>

            {CREW_DOCUMENT_CATEGORIES.map((category) => {
              const uploaded = documents.some(
                (record) => record.type_name === category.type_name,
              );

              return (
                <Box
                  key={category.type_name}
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  gap={1}
                  sx={{ py: 1 }}
                >
                  <Box display="flex" alignItems="center" gap={1}>
                    {uploaded ? (
                      <CheckIcon color="success" sx={{ fontSize: 18 }} />
                    ) : (
                      <WarningIcon
                        color={category.required ? "warning" : "disabled"}
                        sx={{ fontSize: 18 }}
                      />
                    )}
                    <Box>
                      <Typography variant="body2" fontWeight={600}>
                        {category.type_name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {category.description}
                      </Typography>
                    </Box>
                  </Box>
                  <Chip
                    label={
                      uploaded
                        ? "Uploaded"
                        : category.required
                          ? "Missing"
                          : "Optional"
                    }
                    size="small"
                    color={
                      uploaded
                        ? "success"
                        : category.required
                          ? "warning"
                          : "default"
                    }
                    variant={uploaded || category.required ? "filled" : "outlined"}
                    sx={{ borderRadius: 1, flexShrink: 0 }}
                  />
                </Box>
              );
            })}
          </CardContent>
        </Card>
      </Grid>

      <Grid size={{ xs: 12 }}>
        <Card sx={{ borderRadius: 2, boxShadow: 0 }}>
          <CardContent>
            <Box
              display={"flex"}
              alignItems={"center"}
              sx={{
                pb: "8px",
                borderBottom: 1,
                borderColor: "divider",
                mb: "8px",
              }}
              gap={"4px"}
            >
              <WorkOutlineIcon color="disabled" sx={{ fontSize: 20 }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Work Experience
              </Typography>
            </Box>

            {workExperience.length === 0 ? (
              <Typography variant="body2" color="text.secondary" py={1}>
                No work experience recorded yet.
              </Typography>
            ) : (
              workExperience.map((record) => (
                <Box
                  key={record.id}
                  sx={{
                    padding: 1.5,
                    mb: 1,
                    borderRadius: 2,
                    border: "1px solid",
                    borderColor: backgroundColor,
                  }}
                >
                  <Box
                    display="flex"
                    alignItems="flex-start"
                    justifyContent="space-between"
                    gap={1}
                    flexWrap="wrap"
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="body2" fontWeight={600}>
                        {record.position}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {record.company_name}
                      </Typography>
                    </Box>
                    <Box
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexShrink={0}
                    >
                      <Typography variant="caption" color="text.secondary">
                        {formatMonth(record.start_date, "Unknown")} —{" "}
                        {formatMonth(record.end_date, "Present")}
                      </Typography>
                      {!record.end_date && (
                        <Chip
                          label="Current"
                          size="small"
                          color="success"
                          sx={{ borderRadius: 1 }}
                        />
                      )}
                    </Box>
                  </Box>

                  {record.description && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mt: 1, whiteSpace: "pre-line" }}
                    >
                      {record.description}
                    </Typography>
                  )}
                </Box>
              ))
            )}
          </CardContent>
        </Card>
      </Grid>
      </Grid>
    </>
  );
};

export default PilotOverview;
